'use client';

import { trpc } from '@/lib/trpc';

/**
 * Query hooks for the authenticated entrant dashboard
 *
 * These hooks wrap tRPC queries for the /my/dashboard page.
 * Server data is managed by TanStack Query; UI state by entrant-dashboard-store.
 */

/**
 * Query hook for fetching dashboard profile for header display
 * Returns username, name, year started, current event, and vehicle info
 */
export function useMyDashboardProfile() {
  return trpc.myDashboard.profile.useQuery();
}

/**
 * Query hook for fetching dashboard statistics
 * Returns total raised, donation count, goal progress, etc.
 */
export function useMyDashboardStats(opts?: { currencyCode?: string; enabled?: boolean }) {
  const { currencyCode, ...queryOpts } = opts ?? {};
  const input = currencyCode ? { currencyCode } : undefined;
  return trpc.myDashboard.stats.useQuery(input, queryOpts);
}

export function useMyDashboardAchievements() {
  return trpc.myDashboard.achievements.useQuery();
}

/**
 * Query hook for fetching current campaign/event participation
 * Returns the most recent active participation info
 */
export function useMyCurrentCampaign() {
  return trpc.myDashboard.currentCampaign.useQuery(undefined, {
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useMyActiveCampaignSummary() {
  return trpc.myDashboard.activeCampaignSummary.useQuery();
}

/**
 * Query hook for fetching event logistics for schedule/meeting-point card
 */
export function useMyDashboardEventDetails() {
  return trpc.myDashboard.eventDetails.useQuery();
}

/**
 * Query hook for fetching entrant event history with progress per event.
 */
export function useMyEventHistory() {
  return trpc.myDashboard.eventHistory.useQuery();
}

export function useGoalPresets(currency: string, presetsUsd: number[]) {
  return trpc.myDashboard.goalPresets.useQuery(
    { currency, presetsUsd, useCampaignDefault: true },
    { staleTime: 5 * 60 * 1000 },
  );
}

/**
 * Returns a signed check-in QR token for the entrant's current registered event,
 * or null if no active event or the event does not have QR check-in enabled.
 */
export function useMyCheckinToken() {
  return trpc.myDashboard.checkinToken.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSetMyGoal() {
  const utils = trpc.useUtils();

  return trpc.myDashboard.setGoal.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.myDashboard.stats.invalidate(),
        utils.myDashboard.eventHistory.invalidate(),
      ]);
    },
  });
}

/**
 * Mutation hook for changing the entrant's active ride.
 */
export function useChangeMyRide() {
  const utils = trpc.useUtils();

  return trpc.myDashboard.changeRide.useMutation({
    onSuccess: () => {
      void utils.myDashboard.eventHistory.invalidate();
      void utils.myDashboard.currentCampaign.invalidate();
      void utils.myDashboard.profile.invalidate();
      void utils.myDashboard.eventDetails.invalidate();
    },
  });
}

export function useTrackMyProfileShare() {
  const utils = trpc.useUtils();

  return trpc.myDashboard.trackProfileShare.useMutation({
    retry: 2,
    onSuccess: async () => {
      await utils.myDashboard.achievements.invalidate();
    },
  });
}

/**
 * Query hook for fetching paginated donation history
 */
export function useMyDonations(options: { limit?: number; offset?: number } = {}) {
  const { limit = 20, offset = 0 } = options;

  return trpc.myDashboard.donations.useQuery(
    { limit, offset },
    {
      // Keep previous data while fetching new page
      placeholderData: (previousData) => previousData,
      staleTime: 0,
      refetchOnMount: 'always' as const,
    },
  );
}

/**
 * Prefetch dashboard data for faster initial load
 * Call this in a parent component or layout to warm the cache
 */
export function usePrefetchDashboard() {
  const utils = trpc.useUtils();

  return {
    prefetchProfile: () => utils.myDashboard.profile.prefetch(),
    prefetchStats: () => utils.myDashboard.stats.prefetch(),
    prefetchCurrentCampaign: () => utils.myDashboard.currentCampaign.prefetch(),
    prefetchDonations: () => utils.myDashboard.donations.prefetch({ limit: 20, offset: 0 }),
  };
}
